/**
 * @ipc/validation · src/schemas/tenants.ts — формы `/admin/tenants`
 * (раздел 12 «Мультитенантность» × раздел 09 права `tenants.read` / `tenants.manage`).
 *
 * DTO списка/карточки безопасен для выдачи: секреты либо опущены
 * (`multicard.secret` отсутствует), либо замаскированы. Маскирование —
 * `apps/backend/src/domains/tenants/tenant-dto.ts` (`toTenantDto`).
 *
 * Источник правды — таблица Tenant (platform-БД). POST создаёт тенанта
 * (провиженинг БД + migrate + seed ролей). PATCH — метаданные/ротация секретов.
 */
import { z } from 'zod/v4';

const slugSchema = z
  .string()
  .min(2)
  .max(32)
  .regex(/^[a-z][a-z0-9-]{1,31}$/, 'slug must match ^[a-z][a-z0-9-]{1,31}$');

const dbUrlSchema = z.string().refine(
  (value) => {
    try {
      const url = new URL(value);
      return url.protocol === 'postgres:' || url.protocol === 'postgresql:';
    } catch {
      return false;
    }
  },
  { message: 'dbUrl must be a parseable postgres:// or postgresql:// URL' },
);

export const tenantDtoSchema = z.object({
  slug: z.string(),
  name: z.string(),
  domains: z.array(z.string()),
  default: z.boolean(),
  dbUrl: z.string(), // замаскировано: host[:port]/dbName
  apiKey: z.string(), // замаскировано: первые 4 символа + «…»
  logoUrl: z.string(),
  multicard: z.object({
    sandbox: z.boolean(),
    applicationId: z.string(), // замаскировано: первые 4 символа + «…»
    storeId: z.number().int().positive().optional(),
  }),
});
export type TenantDto = z.infer<typeof tenantDtoSchema>;

export const tenantListSchema = z.array(tenantDtoSchema);

export const tenantSlugParamSchema = z.object({
  slug: slugSchema,
});
export type TenantSlugParamDto = z.infer<typeof tenantSlugParamSchema>;

/**
 * POST /admin/tenants — создать тенанта.
 * dbUrl опционален: если нет — авто-CREATE DATABASE ipc_{slug} на хосте default-тенанта.
 * apiKey опционален: если нет — генерируется сервером (возвращается замаскированным;
 * полный ключ один раз в create-response.plainApiKey).
 */
export const createTenantSchema = z.object({
  slug: slugSchema,
  name: z.string().min(1).max(200),
  domains: z.array(z.string().min(1).max(253)).max(32).optional(),
  logoUrl: z.string().max(2048).optional(),
  multicard: z.object({
    applicationId: z.string().min(1).max(256),
    secret: z.string().min(1).max(512),
    storeId: z.number().int().positive(),
    sandbox: z.boolean().optional(),
  }),
  apiKey: z.string().min(16).max(512).optional(),
  dbUrl: dbUrlSchema.optional(),
  isDefault: z.boolean().optional(),
});
export type CreateTenantDto = z.infer<typeof createTenantSchema>;

/** Ответ create: DTO + одноразовый plaintext apiKey (если сгенерирован сервером). */
export const createTenantResponseSchema = z.object({
  tenant: tenantDtoSchema,
  plainApiKey: z.string().optional(),
});
export type CreateTenantResponseDto = z.infer<typeof createTenantResponseSchema>;

/** PATCH /admin/tenants/:slug — метаданные; секреты только если непустая строка. */
export const updateTenantSchema = z
  .object({
    name: z.string().min(1).max(200).optional(),
    domains: z.array(z.string().min(1).max(253)).max(32).optional(),
    logoUrl: z.string().max(2048).optional(),
    multicard: z
      .object({
        sandbox: z.boolean().optional(),
        applicationId: z.string().min(1).max(256).optional(),
        secret: z.string().min(1).max(512).optional(),
        storeId: z.number().int().positive().optional(),
      })
      .optional(),
    apiKey: z.string().min(16).max(512).optional(),
  })
  .refine(
    (v) =>
      v.name !== undefined ||
      v.domains !== undefined ||
      v.logoUrl !== undefined ||
      v.multicard !== undefined ||
      v.apiKey !== undefined,
    { message: 'Нужно хотя бы одно поле для обновления' },
  );
export type UpdateTenantDto = z.infer<typeof updateTenantSchema>;
